import { QueryKeys } from '@common/queries/common';
import { Entity } from '@common/types/Entity';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { BuildingEIcon, CheckIcon, EditBIcon, XCloseIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, growInputCss, Input, Spinner, Stack, StyledInput, toast } from '@prophecy/ui-v3';
import { InputWrap, StyledInputContainer } from '@prophecy/ui-v3/Input/styled';
import { Text } from '@prophecy/ui-v3/Typography';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { validateEntityName } from '@prophecy/utils/validation';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from 'frontend/core/src/context/user';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { updateWorkspaceName } from './api';

export const WorkspaceName = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, currentWorkspaceId } = useUserWorkspace();
  const userInfo = useUser();
  const currentWorkspaceName = currentWorkspace?.workspaceAlias || currentWorkspace?.name;

  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);

  const {
    control,
    trigger,
    getValues,
    reset,
    formState: { errors }
  } = useForm({ defaultValues: { name: currentWorkspaceName ?? '' }, mode: 'onChange' });
  const [saving, setSaving] = useState(false);

  const isOwnWorkspace = currentWorkspace?.createdBy === userInfo?.user?._id;

  useEffect(() => {
    reset({ name: currentWorkspaceName ?? '' });
  }, [currentWorkspaceName, reset]);

  const handleSave = usePersistentCallback(async () => {
    const isValid = await trigger('name');
    if (!isValid) {
      return;
    }

    const { name } = getValues();
    const normalizedWorkspaceName = name?.trim() ?? '';
    const normalizedCurrentWorkspaceName = currentWorkspaceName?.trim() ?? '';
    if (!currentWorkspaceId || !normalizedWorkspaceName || normalizedWorkspaceName === normalizedCurrentWorkspaceName) {
      setIsEditingWorkspaceName(false);
      return;
    }
    setSaving(true);

    try {
      await updateWorkspaceName(currentWorkspaceId, normalizedWorkspaceName);
      toast.success({ content: 'Workspace name updated successfully', 'data-test-id': 'workspace-name-updated' });
      await queryClient.invalidateQueries([Entity.User, QueryKeys.TeamList]);
    } catch (error) {
      toast.error({ content: 'Error updating workspace name', 'data-test-id': 'error-updating-workspace-name' });
    } finally {
      setIsEditingWorkspaceName(false);
      setSaving(false);
    }
  });

  const handleCancel = usePersistentCallback(() => {
    setIsEditingWorkspaceName(false);
    reset({ name: currentWorkspaceName ?? '' });
  });

  const handleKeyDown = usePersistentCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  });

  if (isEditingWorkspaceName) {
    return (
      <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
        <BuildingEIconWrapper>
          <BuildingEIcon type='default' />
        </BuildingEIconWrapper>
        <Controller
          control={control}
          name='name'
          rules={{
            validate: (value) => {
              const normalizedWorkspaceName = value?.trim() ?? '';
              if (!normalizedWorkspaceName) {
                return 'Workspace name is required';
              }
              return validateEntityName(normalizedWorkspaceName);
            }
          }}
          render={({ field }) => (
            <WorkspaceInputWrapper direction='horizontal' gap={theme.spaces.x4} alignY='center'>
              <StyledWorkspaceNameInput
                inputSize='s'
                {...field}
                autoFocus
                variant={errors.name ? 'error' : 'primary'}
                onKeyDown={handleKeyDown}
                data-test-id='workspace-name-input'
              />
              <Button
                size='s'
                variant='linkGray'
                icon={<StyledXCloseIcon type='default' />}
                aria-label='Cancel editing workspace name'
                data-test-id='cancel-workspace-name-button'
                onMouseDown={(e) => e.preventDefault()} // keep input focused until click
                onClick={handleCancel}
              />
              <Button
                size='s'
                variant='linkGray'
                icon={<StyledCheckIcon type='default' />}
                aria-label='Save workspace name'
                data-test-id='save-workspace-name-button'
                loading={saving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSave}
              />
              {errors.name?.message && (
                <ErrorText level='xs' tone={theme.colors.error500}>
                  {errors.name.message}
                </ErrorText>
              )}
            </WorkspaceInputWrapper>
          )}
        />
      </Stack>
    );
  }

  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
      <BuildingEIconWrapper>
        <BuildingEIcon type='default' />
      </BuildingEIconWrapper>
      <Text level='lg' weight={theme.fontWeight.medium}>
        {currentWorkspaceName}
      </Text>
      {isOwnWorkspace ? (
        saving ? (
          <Spinner size='s' />
        ) : (
          <Button
            size='xs'
            variant='linkGray'
            icon={<EditBIcon type={'default'} />}
            aria-label='Edit workspace name'
            data-test-id='edit-workspace-name-button'
            onClick={() => setIsEditingWorkspaceName(true)}
          />
        )
      ) : null}
    </Stack>
  );
};

const BuildingEIconWrapper = styled(Stack)`
  color: ${theme.colors.blue600};
  border: 1px solid ${theme.colors.blue200};
  border-radius: ${theme.radius.x6};
  padding: ${theme.spaces.x8};
  background-color: ${theme.colors.blue50};
`;

const workspaceNameTextStyle = css`
  font-size: ${theme.text.lg.fontSize};
  line-height: ${theme.text.lg.lineHeight};
  font-weight: ${theme.fontWeight.medium};
`;

const StyledWorkspaceNameInput = styled(Input)`
  width: auto;
  max-width: 49rem;
  ${growInputCss}

  /* Sizer + visible text share the same font */
  ${InputWrap} {
    ${workspaceNameTextStyle}
    &::after,
    ${StyledInput} {
      font: inherit;
      min-width: 1ch;
    }
  }
  ${StyledInputContainer} {
    width: auto;
    height: auto;
    &,
    &:focus-within {
      padding: ${theme.spaces.x4} ${theme.spaces.x6};
    }
  }
`;

const StyledCheckIcon = styled(CheckIcon)`
  color: ${theme.colors.success600};

  &:hover {
    color: ${theme.colors.success800};
  }
`;

const StyledXCloseIcon = styled(XCloseIcon)`
  color: ${theme.colors.error600};

  &:hover {
    color: ${theme.colors.error800};
  }
`;

const ErrorText = styled(Text)`
  color: ${theme.colors.error500};
  position: absolute;
  left: 0;
  top: 40px;
  width: 32rem;
`;

const WorkspaceInputWrapper = styled(Stack)`
  position: relative;
`;
